import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { Palette, STAGE_BG_TEXTURE } from '@core/config/constants';
import { CHARACTERS } from '@core/config/characters';
import { MemoryConfig, MEMORY_DIFFICULTIES } from '@modules/memory/config';

/** Una carta del tablero: su personaje y los objetos visuales asociados. */
interface MemoryCard {
  readonly characterKey: string;
  readonly container: Phaser.GameObjects.Container;
  readonly face: Phaser.GameObjects.Graphics;
  readonly portrait: Phaser.GameObjects.Image;
  readonly back: Phaser.GameObjects.Text;
  faceUp: boolean;
  matched: boolean;
}

/** Relacion ancho/alto de cada carta (mas alta que ancha). */
const CARD_ASPECT = 0.78;

/**
 * Minijuego de memoria: se dan vuelta dos cartas por turno buscando las parejas de
 * las chicas. Cuantos menos movimientos, mas puntos. Comparte el selector de
 * dificultad y la pantalla de resultados con el resto de los minijuegos.
 */
export class MemoryScene extends BaseScene {
  private cardWidth = 120;
  private cardHeight = 154;
  private cards: MemoryCard[] = [];
  private firstCard: MemoryCard | null = null;
  private lockInput = false;
  private moves = 0;
  private matched = 0;
  private totalPairs = 0;
  private score = 0;

  private movesText!: Phaser.GameObjects.Text;
  private pairsText!: Phaser.GameObjects.Text;

  public constructor() {
    super(MemoryConfig.key);
  }

  public create(): void {
    this.initResponsive();
    this.addBackground(STAGE_BG_TEXTURE);
    this.addTwinkles(18);
    this.showDifficultyPicker(MemoryConfig.title, MEMORY_DIFFICULTIES, (index) => this.beginRound(index));
  }

  private beginRound(difficultyIndex: number): void {
    const difficulty = MEMORY_DIFFICULTIES[difficultyIndex];
    this.totalPairs = Math.min(difficulty.pairs, CHARACTERS.length);
    this.moves = 0;
    this.matched = 0;
    this.score = 0;
    this.firstCard = null;
    this.lockInput = false;
    this.cards = [];

    const { width } = this.scale;

    this.addBackButton();

    this.movesText = this.add
      .text(this.centerX, 30, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '28px',
        color: Palette.textLight,
      })
      .setOrigin(0.5, 0);

    this.pairsText = this.add
      .text(width - 40, 30, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '28px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);

    this.refreshHud();
    this.buildBoard();
  }

  /** Arma el mazo (parejas mezcladas) y distribuye las cartas en una grilla responsive. */
  private buildBoard(): void {
    const chosen = Phaser.Utils.Array.Shuffle([...CHARACTERS]).slice(0, this.totalPairs);
    const deck = Phaser.Utils.Array.Shuffle(chosen.flatMap((character) => [character, character]));

    const { width, height } = this.scale;
    const count = deck.length;
    const columns = count <= 6 ? 3 : 4;
    const rows = Math.ceil(count / columns);

    const gap = Math.max(12, width * 0.02);
    const areaTop = height * 0.14;
    const areaBottom = height - 48;
    const areaHeight = areaBottom - areaTop;

    const maxCardWidth = (width * 0.94 - (columns - 1) * gap) / columns;
    const maxCardHeight = (areaHeight - (rows - 1) * gap) / rows;
    this.cardWidth = Math.min(maxCardWidth, maxCardHeight * CARD_ASPECT);
    this.cardHeight = this.cardWidth / CARD_ASPECT;

    const totalWidth = columns * this.cardWidth + (columns - 1) * gap;
    const totalHeight = rows * this.cardHeight + (rows - 1) * gap;
    const startX = this.centerX - totalWidth / 2 + this.cardWidth / 2;
    const startY = areaTop + (areaHeight - totalHeight) / 2 + this.cardHeight / 2;

    deck.forEach((character, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const x = startX + column * (this.cardWidth + gap);
      const y = startY + row * (this.cardHeight + gap);
      this.createCard(character.key, character.texture, character.color, x, y, index * 40);
    });
  }

  private createCard(
    characterKey: string,
    texture: string,
    color: number,
    x: number,
    y: number,
    entranceDelay: number,
  ): void {
    const container = this.add.container(x, y);

    const face = this.add.graphics();
    const portrait = this.add.image(0, -this.cardHeight * 0.02, texture).setOrigin(0.5);
    const portraitScale = (this.cardHeight * 0.84) / portrait.height;
    portrait.setScale(portraitScale);
    portrait.setVisible(false);

    const back = this.add
      .text(0, 0, '✦', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: `${Math.round(this.cardHeight * 0.4)}px`,
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    container.add([face, portrait, back]);
    container.setSize(this.cardWidth, this.cardHeight);
    container.setInteractive(
      new Phaser.Geom.Rectangle(-this.cardWidth / 2, -this.cardHeight / 2, this.cardWidth, this.cardHeight),
      Phaser.Geom.Rectangle.Contains,
    );

    const card: MemoryCard = { characterKey, container, face, portrait, back, faceUp: false, matched: false };
    this.paintFace(card, color);

    container.on(Phaser.Input.Events.POINTER_DOWN, () => this.onCardClick(card, color));

    container.setScale(0.6);
    container.setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 260,
      delay: entranceDelay,
      ease: 'Back.easeOut',
    });

    this.cards.push(card);
  }

  /** Redibuja el fondo de la carta segun este boca arriba (color del personaje) o abajo. */
  private paintFace(card: MemoryCard, color: number): void {
    const fill = card.faceUp ? color : Palette.secondary;
    const w = this.cardWidth;
    const h = this.cardHeight;
    card.face.clear();
    card.face.fillStyle(fill, 1);
    card.face.fillRoundedRect(-w / 2, -h / 2, w, h, 18);
    card.face.lineStyle(4, Palette.highlight, card.faceUp ? 0.9 : 0.35);
    card.face.strokeRoundedRect(-w / 2, -h / 2, w, h, 18);
    card.portrait.setVisible(card.faceUp);
    card.back.setVisible(!card.faceUp);
  }

  private onCardClick(card: MemoryCard, color: number): void {
    if (this.lockInput || card.faceUp || card.matched) {
      return;
    }

    this.audio.playClick();
    this.flip(card, true, color);

    if (!this.firstCard) {
      this.firstCard = card;
      return;
    }

    const first = this.firstCard;
    this.firstCard = null;
    this.moves += 1;
    this.refreshHud();

    if (first.characterKey === card.characterKey) {
      this.handleMatch(first, card);
    } else {
      this.handleMismatch(first, card, color);
    }
  }

  private handleMatch(first: MemoryCard, second: MemoryCard): void {
    first.matched = true;
    second.matched = true;
    this.matched += 1;
    this.score += MemoryConfig.pointsPerMatch;
    this.audio.playCorrect();
    this.refreshHud();

    [first, second].forEach((card) => {
      this.tweens.add({
        targets: card.container,
        scale: { from: 1, to: 1.12 },
        duration: 180,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });
    });

    if (this.matched >= this.totalPairs) {
      this.lockInput = true;
      this.time.delayedCall(560, () => this.endRound());
    }
  }

  private handleMismatch(first: MemoryCard, second: MemoryCard, secondColor: number): void {
    this.lockInput = true;
    this.audio.playWrong();
    const firstColor = this.colorOf(first.characterKey);
    this.time.delayedCall(720, () => {
      this.flip(first, false, firstColor);
      this.flip(second, false, secondColor);
      this.lockInput = false;
    });
  }

  private flip(card: MemoryCard, up: boolean, color: number): void {
    card.faceUp = up;
    this.tweens.add({
      targets: card.container,
      scaleX: 0,
      duration: 110,
      ease: 'Sine.easeIn',
      onComplete: () => {
        this.paintFace(card, color);
        this.tweens.add({ targets: card.container, scaleX: 1, duration: 110, ease: 'Sine.easeOut' });
      },
    });
  }

  private colorOf(characterKey: string): number {
    return CHARACTERS.find((character) => character.key === characterKey)?.color ?? Palette.secondary;
  }

  private refreshHud(): void {
    this.movesText.setText(`Movimientos: ${this.moves}`);
    this.pairsText.setText(`Parejas: ${this.matched}/${this.totalPairs}`);
  }

  private endRound(): void {
    const idealMoves = this.totalPairs * 2;
    const savedMoves = Math.max(0, idealMoves - this.moves);
    const finalScore = this.score + savedMoves * MemoryConfig.bonusPerSavedMove;

    const isRecord = this.profile.submitScore(MemoryConfig.key, finalScore);
    const coins = Math.floor(finalScore / 100) * MemoryConfig.coinsPer100Points;
    this.profile.addCoins(coins);
    this.audio.playReward();

    this.showRoundResults({
      title: isRecord ? '¡Nuevo record!' : '¡Lo lograste!',
      score: finalScore,
      coins,
    });
  }
}
